import React, { useState, useRef } from "react";
import {
  Box,
  Fab,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  Card,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { fetchTodayPhotoLength } from "../Hooks/Api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { TokenAtom, TokenInfo, NewPhotoStates } from "../Hooks/TokenAtom";


import { createTheme, ThemeProvider } from "@mui/material/styles";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";


const UploadButtonCam = ({ onUploadSuccess }) => {
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]); // 파일 -> 동영상으로 변경
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");

  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const tokens = useRecoilValue(TokenAtom);
  const tokenInfo = useRecoilValue(TokenInfo);
  const setTodayPhotosCount = useSetRecoilState(NewPhotoStates);
  const memoRef = useRef();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setVideos([]); // 초기화
    if (memoRef.current) {
      memoRef.current.value = "";
    }
    setTitle("");
    setOpen(false);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setDatePickerOpen(false);
  };

  const handleVideoChange = (event) => {
    const newVideos = Array.from(event.target.files);
    setVideos((prevVideos) => [...prevVideos, ...newVideos]);
  };

  const handleUpload = async () => {
    setLoading(true);

    const formData = new FormData();
    formData.append("writer", tokenInfo.first_name);
    formData.append("title", title);
    formData.append("memo", memoRef.current.value);
    formData.append("date", selectedDate.format("YYYY-MM-DD"));
    formData.append("dateToday", dayjs().format("YYYY-MM-DD"));

    videos.forEach((video) => {
      formData.append("video", video); // 변경: 파일 -> 비디오로 업로드
    });

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(percentComplete);
      }
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        setLoading(false);
        if (xhr.status === 201) {
          onUploadSuccess(xhr.status);
          setVideos([]);
          setUploadProgress(0);
          fetchTodayPhotoLength(tokens, setTodayPhotosCount);
          handleClose();
        } else {
          console.error("Error uploading videos:", xhr.responseText);
          onUploadSuccess(xhr.status); // 업로드 성공 시 호출

          setVideos([]);
          setUploadProgress(0);
          handleClose();
        }
      }
    };

    xhr.open("POST", `/api/UploadPhotoCam`, true); // 엔드포인트 변경
    xhr.setRequestHeader("Authorization", `Bearer ${tokens.access}`);
    xhr.send(formData);
  };

  const handleRemove = (videoToRemove) => {
    setVideos((prevVideos) =>
      prevVideos.filter((video) => video !== videoToRemove)
    );
  };
  const pickerTheme = createTheme({
    palette: {
      primary: { main: "#8c6744" },
      secondary: { main: "#cf9e62" },
    },
    typography: {
      fontFamily: "LeeSeoyun, sans-serif",
    },
  });
  return (
    <Box sx={{ position: "fixed", right: 16, bottom: 32 }}>
      <Fab
        aria-label="add"
        onClick={handleClickOpen}
        size="small"
        sx={{
          background: "linear-gradient(45deg, #8c6744, #cf9e62)",
          color: "white",
          "&:hover": {
            background: "linear-gradient(45deg, #a67855, #f8e5d0)",
          },
        }} // 캘린더와 동일한 높이 설정
      >
        <AddIcon />
      </Fab>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#f5f4f2", // 전체 배경 색
            borderRadius: "12px", // 모서리 둥글게
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: "#8c6744" }}>
          동영상 업로드
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#f5f4f2", pt: "16px !important" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {uploadProgress}%
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1, // 버튼 간 간격
                }}
              >
                <TextField
                  type="file"
                  fullWidth
                  onChange={handleVideoChange}
                  inputProps={{ accept: "video/*", multiple: true }} // 변경: accept 속성
                  sx={{ display: "none" }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    sx={{
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#B8860B", // 민트 → 초록
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        background: "#DAA520", // hover 버전
                      },
                    }}
                  >
                    <VideoLibraryIcon sx={{ fontSize: 20, color: "white" }} />
                  </Button>
                </label>
                <ThemeProvider theme={pickerTheme}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="YYYY-MM-DD"
                      label="날짜 선택"
                      value={selectedDate}
                      onChange={handleDateChange}
                      open={datePickerOpen}
                      onOpen={() => setDatePickerOpen(true)}
                      onClose={() => setDatePickerOpen(false)}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            width: "100%",
                            minHeight: "40px", // 높이 최소 확보
                            backgroundColor: "white", // ← 폼 배경 하얗게
                            borderRadius: "8px", // ← 둥근 모서리(선택)

                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#8c6744",
                            },

                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#8c6744",
                            },

                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#8c6744",
                            },
                            "& .MuiInputBase-input": {
                              backgroundColor: "white",
                              color: "#8c6744",
                            },
                            "& .MuiFormLabel-root": {
                              color: "#8c6744",
                            },
                            "& .MuiFormLabel-root.Mui-focused": {
                              color: "#8c6744", // 예: 강조색
                            },
                            "& .MuiSvgIcon-root path": {
                              fill: "#8c6744",
                            },
                          },
                        },
                        actionBar: {
                          actions: ["today"],
                        },
                      }}
                    />
                  </LocalizationProvider>
                </ThemeProvider>
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "0.8rem",
                  color: "#8c6744",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mb: 1,
                }}
              >
                첨부 동영상: {videos.length}개
              </Typography>
              <Box sx={{ mb: 1 }}>
                <TextField
                  label="제목"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  inputProps={{ maxLength: 20 }}
                  helperText={`${title.length}/20`}
                  FormHelperTextProps={{ sx: { textAlign: "right", color: title.length >= 20 ? "#c0392b" : "#8c6744", m: 0 } }}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f4f2",
                      "& fieldset": {
                        borderColor: "#c9b08a",
                      },
                      "&:hover fieldset": {
                        borderColor: "#b89263",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#8c6744",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#8c6744",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#8c6744",
                    },
                    /* ✨ 입력 텍스트 색상 변경 */
                    "& .MuiInputBase-input": {
                      color: "#8c6744", // ← 원하는 텍스트 색상
                    },
                  }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="메모"
                  fullWidth
                  multiline
                  rows={2}
                  inputRef={memoRef} // ⭐ 여기 핵심
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f4f2",
                      "& fieldset": {
                        borderColor: "#c9b08a",
                      },
                      "&:hover fieldset": {
                        borderColor: "#b89263",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#8c6744",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#8c6744",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#8c6744",
                    },
                    /* ✨ 입력 텍스트 색상 변경 */
                    "& .MuiInputBase-input": {
                      color: "#8c6744", // ← 원하는 텍스트 색상
                    },
                  }}
                />
              </Box>
              <Box>
                <Grid container spacing={2}>
                  {videos.length === 0 ? (
                    <Box
                      sx={{
                        width: "100%",
                        textAlign: "center",
                        py: 2,
                        color: "#8c6744",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#8c6744" }}>
                        업로드할 동영상이 없습니다.
                      </Typography>
                    </Box>
                  ) : (
                    videos.map((video, index) => (
                      <Grid item xs={4} key={index}>
                        <Card
                          sx={{
                            position: "relative",
                            overflow: "hidden",
                            width: "100%",
                            height: 100,
                          }}
                        >
                          <video
                            src={URL.createObjectURL(video)}
                            controls
                            width="100%"
                            height="100%"
                            style={{ objectFit: "cover" }}
                          />

                          <IconButton
                            onClick={() => handleRemove(video)}
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              backgroundColor: "rgba(184, 134, 11, 0.6)", // 반투명 골드
                              color: "white", // 아이콘 색상
                              width: 28,
                              height: 28,
                              "&:hover": {
                                backgroundColor: "#B8860B", // hover 시 진한 골드
                              },
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpload}
            disabled={loading}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              background: "#B8860B",
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520",
              },
              "&.Mui-disabled": {
                background: "#c9a84c",
                color: "white",
              },
            }}
          >
            {loading ? "업로드 중..." : "업로드"}
          </Button>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B",
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
              "&.Mui-disabled": {
                borderColor: "#c9b08a",
                color: "#c9b08a",
              },
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadButtonCam;
